import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, Upload, FileText, Image as ImageIcon, X } from "lucide-react";
import { WizardProgress, Button, SkeletonText, AnalystReviewingIndicator, Field, Input, Select, Textarea } from "@/design/components";
import { formatHumanCurrency } from "@/design/format";
import { cn } from "@/design/cn";
import { useSeed, useActorId, useActiveProfile, useSubProjects, useMilestones, useSubProjectMilestones, useAuthorityGrants, useMoney, resolveSelfAuthThreshold, capturePath, useAnalystName } from "@/domain/hooks";
import { useRaisableProjects } from "@/domain/governance";
import { useStore } from "@/domain/store";
import { useOnlineStatus } from "@/domain/useOnlineStatus";
import { useFileDrop } from "@/domain/useFileDrop";
import { runAIReview } from "@/domain/mutations";
import { useRaiseSpend } from "@/domain/useMutations";
import { SPEND_CATEGORIES, type SpendCategory, type AttachedDocument } from "@/domain/types";

// A real document picked from the device while raising the ask. Image attachments get an
// object-URL thumbnail; everything else shows a file chip. The File is kept so we can
// persist a durable data URL on submit — an object URL dies with this screen, which would
// leave the attachment unviewable when a contractor opens the ask.
type Doc = { id: string; name: string; url?: string; kind: AttachedDocument["kind"]; file: File };

function docKind(file: File): AttachedDocument["kind"] {
  // Detect by MIME type, falling back to the extension — browsers (notably iOS Safari)
  // often report an empty file.type for camera photos, which would otherwise misclassify
  // a JPEG as a generic document and leave it un-previewable.
  if (file.type.startsWith("image/") || /\.(jpe?g|png|gif|webp|bmp|hei[cf]|avif|svg)$/i.test(file.name)) return "image";
  if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) return "pdf";
  if (/\.(xlsx|xls|csv)$/i.test(file.name)) return "xlsx";
  return "doc";
}

// Read a picked file into a base64 data URL. Unlike an object URL, this survives the raise
// screen unmounting, so the persisted attachment stays viewable in the approval drawer.
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const THRESHOLD = 400_000_000;

const STEPS = [
  { key: "basics", label: "Basics" },
  { key: "purpose", label: "Purpose" },
  { key: "confirm", label: "Confirm" },
];

/** The canonical raise-an-ask wizard. lensScope controls the exit target and footer offset
 *  so the same component serves the reporter (mobile) and the contractor lead (workspace). */
export function RaiseAsk({ lensScope = "reporter" }: { lensScope?: "reporter" | "contractor-lead" } = {}) {
  const seed = useSeed();
  const actorId = useActorId();
  const profile = useActiveProfile();
  // Governance: only projects where the active profile holds a raise-capable
  // membership (their assignment) — a reporter never asks against another site.
  const projects = useRaisableProjects();
  const updateSeed = useStore((s) => s.updateActiveSeed);
  const { raise } = useRaiseSpend();
  const isOnline = useOnlineStatus();
  const simulatedOffline = useStore((s) => s.reporterOffline);
  const offline = simulatedOffline || !isOnline;
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const doneTarget = lensScope === "contractor-lead" ? `/workspace/${workspaceId}` : "/reporter";

  const [step, setStep] = useState(0);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState<SpendCategory>("Materials");
  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");
  const [subProjectId, setSubProjectId] = useState("");
  const [milestoneId, setMilestoneId] = useState("");
  const [purpose, setPurpose] = useState("");
  const [supplier, setSupplier] = useState("");
  const [submitted, setSubmitted] = useState(false);
  // Mirrors the ~2.8s background AI review so the success screen shows it in flight.
  const [reviewing, setReviewing] = useState(false);
  const [docs, setDocs] = useState<Doc[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const analystName = useAnalystName();
  // Every object URL we mint for thumbnails, revoked once on unmount. Tracking in a ref
  // (rather than a [docs] effect) avoids revoking a still-shown thumbnail when another file
  // is added, and never touches the durable data URLs we persist on submit.
  const objectUrls = useRef<string[]>([]);

  // Revoke any image thumbnails when leaving the screen so blobs are not leaked.
  useEffect(() => () => objectUrls.current.forEach((u) => URL.revokeObjectURL(u)), []);

  function addDocs(files: File[]) {
    if (files.length === 0) return;
    setDocs((xs) => [
      ...xs,
      ...files.map((f) => {
        const kind = docKind(f);
        let url: string | undefined;
        if (kind === "image") {
          url = URL.createObjectURL(f);
          objectUrls.current.push(url);
        }
        return {
          id: `doc-${Math.random().toString(36).slice(2, 8)}`,
          name: f.name,
          url,
          kind,
          file: f,
        };
      }),
    ]);
  }

  function onPickDocs(e: React.ChangeEvent<HTMLInputElement>) {
    addDocs(Array.from(e.target.files ?? []));
    e.target.value = "";
  }

  const fileDrop = useFileDrop({ onFiles: addDocs });

  function removeDoc(id: string) {
    setDocs((xs) => {
      const gone = xs.find((d) => d.id === id);
      if (gone?.url) URL.revokeObjectURL(gone.url);
      return xs.filter((d) => d.id !== id);
    });
  }

  // Sub-projects and milestones come from the backend (dual-mode hooks) keyed by
  // the selected project's id, so they stay consistent with the wired project list.
  const subs = useSubProjects(projectId);
  // Milestones follow the sub-project choice: "Whole project" offers the
  // project-level milestones; a sub-project offers only its own.
  const projectMilestones = useMilestones(projectId);
  const subMilestones = useSubProjectMilestones(subProjectId);
  const milestones = subProjectId ? subMilestones : projectMilestones;
  const amountNum = Number(amount.replace(/[^0-9]/g, ""));

  // Cascading authority: the reporter's self-auth threshold for the selected scope decides
  // whether this is recorded-and-evidenced directly or sent up the chain for authorisation.
  const grants = useAuthorityGrants();
  const money = useMoney();
  const myThreshold = resolveSelfAuthThreshold(grants, actorId, { projectId, subProjectId: subProjectId || undefined });
  const path = capturePath(amountNum, myThreshold);

  // The specific unmet requirement for the current step. Null means the step is complete.
  // Continue stays tappable and surfaces this on attempt, so the user is never blocked by a
  // disabled button with a hidden reason (P0-11).
  const [stepBlocker, setStepBlocker] = useState<string | null>(null);

  function stepProblem(i: number): string | null {
    if (i === 0) {
      const missing = [
        !title.trim() && "a title",
        !(amountNum > 0) && "an amount",
        !projectId && "a project",
      ].filter(Boolean);
      if (missing.length) return `Add ${missing.join(", ")} to continue.`;
    }
    if (i === 1) {
      const have = purpose.trim().length;
      if (have < 80) return `The purpose needs at least 80 characters; you have ${have}. Add a little more detail to continue.`;
    }
    return null;
  }

  function advance() {
    const problem = stepProblem(step);
    if (problem) {
      setStepBlocker(problem);
      return;
    }
    setStepBlocker(null);
    setStep((s) => s + 1);
  }

  // Once the user satisfies the requirement, drop the warning without waiting for another tap.
  useEffect(() => {
    if (stepBlocker && stepProblem(step) == null) setStepBlocker(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, amountNum, projectId, purpose, step]);

  async function submit() {
    let newId: string;
    // Persist a durable data URL per document, not the object URL — the latter is revoked
    // when this screen unmounts, so a contractor opening the ask could not view it.
    const now = new Date().toISOString();
    const documents = await Promise.all(
      docs.map(async (d) => ({
        id: `ad-${d.id}`,
        filename: d.name,
        kind: d.kind,
        sizeBytes: d.file.size,
        uploadedAt: now,
        uploadedById: actorId,
        url: await readAsDataUrl(d.file),
      })),
    );
    try {
      newId = await raise({
        reporterId: actorId,
        organisationId: profile.organisationId,
        projectId,
        subProjectId: subProjectId || undefined,
        milestoneId: milestoneId || undefined,
        title: title.trim(),
        amount: amountNum,
        category,
        supplier: supplier.trim() || undefined,
        purpose: purpose.trim(),
        path,
        documents,
      }, docs.map((d) => d.file));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      toast.error(`Could not submit: ${msg}`);
      return;
    }

    // Set the AI review as pending in the local store (Heimdall brief §2).
    if (newId) {
      updateSeed((s) => {
        const next = structuredClone(s);
        const r = next.spendRequests.find((x) => x.id === newId);
        if (r) r.aiReview = { state: "pending", findingIds: [] };
        return next;
      });
    }
    if (offline) {
      // enqueueAction (inside useRaiseSpend) already durably queued this for
      // real replay once back online - nothing to do here but tell the user.
      toast.message("Saved offline. It will sync when you are back online.");
    } else {
      if (newId) {
        const id = newId;
        setReviewing(true);
        window.setTimeout(() => {
          updateSeed((s) => runAIReview(s, id));
          setReviewing(false);
        }, 2800);
      }
      toast.success(
        path === "recorded"
          ? "Spend recorded within your authority. Add evidence when the money is spent."
          : "Sent for authorisation. You will see it in your inbox under 'Awaiting approval'.",
      );
    }
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center px-6 py-12 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-okSoft text-okStrong"><Check className="h-7 w-7" /></span>
        <h2 className="mt-4 text-xl font-semibold text-ink">{path === "recorded" ? "Recorded within your authority" : "Sent for authorisation"}</h2>
        <p className="mt-2 text-base text-mute">
          {path === "recorded"
            ? "Add evidence once the money is spent. Stratus oversight confirms it."
            : "Your contractor lead will review it and approve, query, or reject."}
        </p>
        {/* TODO(heimdall): AI analyst not built yet — re-enable the post-submit review card
            ("{analyst} review complete · checks passed") when Heimdall ships.
        {!offline && (
          <div className="mt-6 w-full rounded-card border border-line bg-card p-4 text-left">
            {reviewing ? (
              <>
                <AnalystReviewingIndicator name={analystName} />
                <SkeletonText lines={2} className="mt-3" />
              </>
            ) : (
              <p className="flex items-center gap-2 text-sm text-ok">
                <Check className="h-4 w-4" /> {analystName} review complete · checks passed
              </p>
            )}
          </div>
        )}
        */}
        <Button size="lg" className="mt-6 w-full" onClick={() => navigate(doneTarget)}>Done</Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b border-line px-4 py-3">
        <WizardProgress steps={STEPS} currentIndex={step} compact />
      </div>

      <div className="flex-1 space-y-5 p-4 pb-24">
        {step === 0 && (
          <>
            <Field label="Title">
              <Input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 80))} placeholder="What is this spend for" className="py-3 text-base" />
            </Field>
            <Field label="Category">
              <Select value={category} onChange={(e) => setCategory(e.target.value as SpendCategory)} className="py-3 text-base">
                {SPEND_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Amount (UGX)">
              <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="numeric" placeholder="5,000,000" className="py-3 text-base" />
              {amountNum > 0 && <p className="mt-1.5 text-sm text-faint">{formatHumanCurrency(amountNum)}</p>}
              {amountNum > 0 && myThreshold > 0 && (
                <p className={`mt-2 rounded-control border-l-4 px-3 py-2 text-sm ${path === "recorded" ? "border-l-ok bg-okSoft text-okStrong" : "border-l-warn bg-warnSoft text-warnStrong"}`}>
                  {path === "recorded"
                    ? `Within your authority (up to ${money(myThreshold)}). You can record this and add evidence directly.`
                    : `Above your ${money(myThreshold)} authority. This will be sent up the chain for authorisation.`}
                </p>
              )}
              {amountNum >= THRESHOLD && (
                <p className="mt-2 flex items-center gap-1.5 rounded-control border border-warn/30 border-l-4 border-l-warn bg-warnSoft px-3 py-2 text-sm text-warnStrong">
                  <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                  Above the 400M UGX Clause 21 threshold. A compliance declaration is required before this can be approved.
                </p>
              )}
            </Field>
            <Field label="Project">
              <Select value={projectId} onChange={(e) => { setProjectId(e.target.value); setSubProjectId(""); setMilestoneId(""); }} className="py-3 text-base">
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </Field>
            <Field label="Sub-project">
              <Select value={subProjectId} onChange={(e) => { setSubProjectId(e.target.value); setMilestoneId(""); }} className="py-3 text-base">
                <option value="">Whole project</option>
                {subs.map((sp) => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
              </Select>
            </Field>
            <Field label="Milestone">
              <Select value={milestoneId} onChange={(e) => setMilestoneId(e.target.value)} className="py-3 text-base">
                <option value="">Select</option>
                {milestones.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </Select>
            </Field>
          </>
        )}

        {step === 1 && (
          <>
            <Field label="Purpose">
              <Textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Briefly explain what the spend is for, why it is needed, and how it advances the milestone." className="h-40 py-3 text-base" />
              <p className="mt-1.5 text-sm text-faint"><span className="data">{purpose.trim().length} / 80</span> characters minimum</p>
            </Field>
            <Field label="Supplier (optional)">
              <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Supplier name" className="py-3 text-base" />
            </Field>
          </>
        )}

        {step === 2 && (
          <>
            <div onDragOver={fileDrop.onDragOver} onDragLeave={fileDrop.onDragLeave} onDrop={fileDrop.onDrop}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xlsx,.xls,.csv"
                multiple
                className="hidden"
                onChange={onPickDocs}
              />
              {docs.length === 0 ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "focus-ring w-full rounded-card border-2 border-dashed py-10 text-center text-sm transition-colors",
                    fileDrop.isDraggingOver ? "border-brand bg-brandSoft text-ink" : "border-line text-faint hover:border-brand hover:text-ink",
                  )}
                >
                  <Upload className="mx-auto mb-2 h-6 w-6" />
                  {fileDrop.isDraggingOver ? "Drop to add" : "Add documents (PDF, DOCX, JPG, PNG), or drag and drop"}
                </button>
              ) : (
                <div className={cn("rounded-card border p-4 transition-colors", fileDrop.isDraggingOver ? "border-brand bg-brandSoft" : "border-line bg-card")}>
                  <div className="mb-2.5 flex items-center justify-between">
                    <p className="text-meta text-faint">Documents</p>
                    <span className="text-sm tabular-nums text-mute">{docs.length} attached</span>
                  </div>
                  <ul className="space-y-2">
                    {docs.map((d) => (
                      <li key={d.id} className="flex items-center gap-3 rounded-control border border-line bg-card px-3 py-2.5">
                        {d.url ? (
                          <img src={d.url} alt={d.name} className="h-10 w-10 shrink-0 rounded object-cover" />
                        ) : d.kind === "image" ? (
                          <ImageIcon className="h-5 w-5 shrink-0 text-faint" aria-hidden />
                        ) : (
                          <FileText className="h-5 w-5 shrink-0 text-faint" aria-hidden />
                        )}
                        <span className="flex-1 truncate text-sm text-ink">{d.name}</span>
                        <button
                          type="button"
                          onClick={() => removeDoc(d.id)}
                          aria-label={`Remove ${d.name}`}
                          className="focus-ring -my-2.5 -mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-faint hover:text-mute"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <Button variant="ghost" size="lg" className="mt-2 w-full" onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-4 w-4" /> Add more
                  </Button>
                </div>
              )}
            </div>
            <div className="rounded-card border border-line bg-card p-4">
              <SummaryRow label="Title" value={title} />
              <SummaryRow label="Category" value={category} />
              <SummaryRow label="Amount" value={formatHumanCurrency(amountNum)} />
              <SummaryRow label="Supplier" value={supplier || "—"} />
              <SummaryRow label="Milestone" value={milestones.find((m) => m.id === milestoneId)?.name ?? "—"} />
              <SummaryRow label={path === "recorded" ? "Authority" : "Approver"} value={path === "recorded" ? "Self-authorised · evidence required after spend" : "Your contractor lead"} />
            </div>

            {/* Pre-submission checklist — fix problems before the approver sees them. */}
            <div className="rounded-card border border-line bg-card p-4">
              <p className="text-meta mb-2 text-faint">Before you submit</p>
              <ul className="space-y-2">
                <CheckItem ok={purpose.trim().length >= 80} text={purpose.trim().length >= 80 ? "Purpose is detailed enough" : "Add more detail to the purpose"} />
                <CheckItem ok={!!supplier.trim()} text={supplier.trim() ? "Supplier named" : "No supplier named (optional, but helps reconciliation)"} />
                <CheckItem ok={!!milestoneId} text={milestoneId ? "Linked to a milestone" : "No milestone linked"} />
                <CheckItem ok text={path === "recorded" ? "Within your authority. You can record and evidence directly" : "Above your authority. Will go for authorisation"} />
              </ul>
              <p className="mt-3 text-sm text-faint">Better first-time submissions mean faster approvals and fewer queries.</p>
            </div>
          </>
        )}
      </div>

      <div className={`sticky ${lensScope === "reporter" ? "bottom-16 md:bottom-0" : "bottom-0"} border-t border-line bg-card`}>
        {stepBlocker && step < 2 && (
          <p role="alert" className="mx-4 mt-3 rounded-control border border-warn/30 bg-warnSoft px-3 py-2 text-sm text-warnStrong">
            {stepBlocker}
          </p>
        )}
        <div className="flex gap-2 p-4">
          {step > 0 && <Button size="lg" variant="ghost" onClick={() => { setStepBlocker(null); setStep((s) => s - 1); }}>Back</Button>}
          {step < 2 ? (
            <Button size="lg" className="flex-1" onClick={advance}>Continue</Button>
          ) : (
            <Button size="lg" className="flex-1" onClick={submit}>{path === "recorded" ? "Record spend" : "Submit for authorisation"}</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function CheckItem({ ok, text }: { ok: boolean; text: string }) {
  return (
    <li className="flex items-start gap-2.5 text-sm">
      <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${ok ? "bg-okSoft text-okStrong" : "bg-warnSoft text-warnStrong"}`}>
        {ok ? <Check className="h-3 w-3" /> : <span className="text-[10px] font-bold">!</span>}
      </span>
      <span className={ok ? "text-okStrong" : "text-warnStrong"}>{text}</span>
    </li>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-hairline py-2.5 last:border-0">
      <span className="text-base text-faint">{label}</span>
      <span className="text-base font-medium text-ink">{value}</span>
    </div>
  );
}
